import React, { useEffect, useState, useCallback } from 'react';
import {
    Button,
    Card,
    Col,
    Container,
    Dropdown,
    Form,
    Image,
    Row,
    Stack,
} from 'react-bootstrap';
import camera from '../../Assets/Img/camera.png';
import '../Profile/profile.scss';
import { CircleUserRound, Plus } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import config from '../../Api/config';
import { formatDate } from '../../helper';
import { usePatientListMutation } from './patientApi';
import LoadingComponent from '../LoadingComponent';
import { setSecureItem, getSecureItem, removeSecureItem } from "../../utils/secureStorage";
const PatientProfile = () => {
    const { t } = useTranslation();
    const { state } = useLocation();
    const maxDateValidation = new Date().toISOString().split('T')[0];
    const user = getSecureItem('UserData');
    const [loading, setLoading] = useState(true);
    const [profileData, setProfileData] = useState({
        ...state?.userData,
        dateOfBirth: formatDate(state?.userData?.dateOfBirth),
    });
    const navigate = useNavigate();
    const [patientList] = usePatientListMutation();
    const iSContactLense = getSecureItem('ContactLense');

    const getAge = useCallback((dobString) => {
        if (!dobString) return '';
        const dob = new Date(dobString);
        const today = new Date();

        let age = today.getFullYear() - dob.getFullYear();
        const monthDiff = today.getMonth() - dob.getMonth();
        const dayDiff = today.getDate() - dob.getDate();

        // Adjust if birthday hasn't occurred yet this year
        if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
            age--;
        }

        return age;
    }, []);

    // const navigateToEyeTest = useCallback((glassType) => {
    //     // Clear any existing patientEmail first
    //     removeSecureItem('patientEmail');

    //     const payload = {
    //         flag: 'fromPatient',
    //         patientEmail: profileData?.email,
    //         name: profileData?.name,
    //         lastName: profileData?.lastName,
    //         dateOfBirth: profileData?.dateOfBirth,
    //         age: getAge(profileData?.dateOfBirth)
    //     };

    //     setSecureItem("patientEmail", payload);

    //     navigate('/eyetest/add', {
    //         state: {
    //             glass: glassType === 'glasses',
    //             patientEmail: profileData?.email,
    //             patientName: profileData?.name,
    //             patientLastName: profileData?.lastName
    //         },
    //     });
    // }, [profileData, navigate, getAge]);
    const navigateToEyeTest = useCallback((glassType) => {
        navigate('/eyetest/add', {
            state: {
                fromPatient: true,
                glass: glassType === 'glasses',
                patientEmail: profileData?.email,
                name: profileData?.name,
                lastName: profileData?.lastName,
                dateOfBirth: profileData?.dateOfBirth,
            },
        });
    }, [profileData, navigate]);

    useEffect(() => {
        const fetchPatientData = async () => {
            if (loading && state?.userData) {
                try {
                    let tempData = {
                        email: state?.userData,
                        mobile: '',
                        search: '',
                        pageSize: 50,
                        pageNumber: 1,
                        startDate: '',
                        endDate: '',
                    };

                    const response = await patientList({ ...tempData });

                    if (response.data?.userProfileData?.[0]) {
                        const patientData = response.data.userProfileData[0];
                        setProfileData({
                            ...patientData,
                            dateOfBirth: formatDate(patientData?.dateOfBirth),
                            createdDate: formatDate(patientData?.createdDate),
                            age: getAge(patientData?.dateOfBirth)
                        });
                    }
                } catch (error) {
                    console.error('Error fetching patient data:', error);
                } finally {
                    setLoading(false);
                }
            } else {
                setLoading(false);
            }
        };

        fetchPatientData();
    }, [loading, state?.userData, patientList, getAge]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            // Optional: Clear patientEmail if not needed
            // removeSecureItem('patientEmail');
        };
    }, []);

    return (
        <>
            {loading ? <LoadingComponent /> : ''}
            <Container fluid>
                <Row className="mb-3">
                    <div className="d-flex p-0 gap-3 justify-content-end">
                        {profileData?.userType === 'PATIENT' ? (
                            <>
                                <Button
                                    onClick={() => {
                                        navigate('/eyetest/list', { state: { fromPatient: true, email: profileData?.email, name: profileData?.name, lastName: profileData?.lastName }, });
                                    }}
                                    variant="primary"
                                >
                                    {t('patientViewAllTests')}
                                </Button>

                                {(user.userType !== 'PATIENT') && (
                                    <Dropdown>
                                        <Dropdown.Toggle
                                            variant="light"
                                            id="dropdown-basic"
                                            className="d-flex align-items-center gap-1"
                                        >
                                            <Plus width={14} height={14} />
                                            {t('patientAddNewEyetest')}
                                        </Dropdown.Toggle>

                                        <Dropdown.Menu>
                                            <Dropdown.Item
                                                onClick={() => navigateToEyeTest('glasses')}
                                            >
                                                {t('glasses')}
                                            </Dropdown.Item>
                                            {iSContactLense === false ? null : (
                                                <Dropdown.Item
                                                    onClick={() => navigateToEyeTest('lenses')}
                                                >
                                                    {t('lenses')}
                                                </Dropdown.Item>
                                            )}
                                        </Dropdown.Menu>
                                    </Dropdown>
                                )}
                            </>
                        ) : (
                            ''
                        )}
                    </div>
                </Row>
            </Container>
            <Container fluid className={`custom_disabled`}>
                <div className="profile_header">
                    <Card className="mb-4">
                        <Card.Body className="modal_form">
                            <h5 className="fw-semibold mb-4">
                                {t('nameAndPicTitle')}
                            </h5>
                            <div className="profile_image mb-4">
                                {profileData?.profileImagePath ? (
                                    <Image
                                        src={`${config.img_URL}/${profileData?.profileImagePath}`}
                                        alt="Profile"
                                        style={{ width: '120px', height: '120px', objectFit: 'cover' }}
                                    />
                                ) : (
                                    <CircleUserRound width={120} height={120} />
                                )}
                                <div
                                    role="button"
                                    className="image_upload_button"
                                    onClick={() => {/* Handle image upload */ }}
                                >
                                    <img
                                        src={camera}
                                        alt="Upload"
                                        style={{
                                            height: '24px',
                                            width: '24px',
                                        }}
                                    />
                                </div>
                            </div>
                            <Row>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>
                                            {t('patientNo')}
                                        </Form.Label>
                                        <Form.Control
                                            value={profileData?.patientUniqueCode || ''}
                                            type="text"
                                            readOnly
                                            disabled
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>
                                            {t('firstName')}
                                        </Form.Label>
                                        <Form.Control
                                            value={profileData?.name || ''}
                                            type="text"
                                            name="name"
                                            readOnly
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>{t('name')}</Form.Label>
                                        <Form.Control
                                            value={profileData?.lastName || ''}
                                            type="text"
                                            name="lastName"
                                            readOnly
                                        />
                                    </Form.Group>
                                </Col>

                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>{t('email')}</Form.Label>
                                        <Form.Control
                                            value={profileData?.email || ''}
                                            type="email"
                                            name="email"
                                            readOnly
                                            disabled
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </div>
                <div className="profile_details">
                    <Card className="mb-4">
                        <Card.Body className="modal_form">
                            <h5 className="fw-semibold mb-4">
                                {t('personalInformation')}
                            </h5>
                            <Row>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>{t('userType')}</Form.Label>
                                        <Form.Control
                                            value={profileData?.userType || ''}
                                            type="text"
                                            readOnly
                                            disabled
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>{t('phone')}</Form.Label>
                                        <Form.Control
                                            value={profileData?.mobile || ''}
                                            type="tel"
                                            name="mobile"
                                            placeholder={t('inputText')}
                                            readOnly
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>
                                            {t('dateofBirth')}
                                        </Form.Label>
                                        <Form.Control
                                            max={maxDateValidation}
                                            value={profileData?.dateOfBirth || ''}
                                            type="date"
                                            name="dateOfBirth"
                                            readOnly
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>{t('Créé le')}</Form.Label>
                                        <Form.Control
                                            max={maxDateValidation}
                                            value={profileData?.createdDate || ''}
                                            type="date"
                                            name="createdDate"
                                            readOnly
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>{t('Crée par')}</Form.Label>
                                        <Form.Control
                                            value={profileData?.createdByMail || ''}
                                            type="text"
                                            name="createdByMail"
                                            readOnly
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>{t('age')}</Form.Label>
                                        <Form.Control
                                            value={profileData?.age || ''}
                                            type="text"
                                            name="age"
                                            readOnly
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>{t('gender')}*</Form.Label>
                                        <Form.Control
                                            value={
                                                profileData?.gender
                                                    ? { Male: "Homme", Female: "Femme", Others: "Autres" }[profileData.gender] || ""
                                                    : ""
                                            }
                                            type="text"
                                            readOnly
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>{t('address')}</Form.Label>
                                        <Form.Control
                                            value={profileData?.doctorAddressOfTheHealthCenter || ''}
                                            type="text"
                                            name="doctorAddressOfTheHealthCenter"
                                            readOnly
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>
                                            {t('patientAdditionalComments')}
                                        </Form.Label>
                                        <Form.Control
                                            value={profileData?.additionalComments || ''}
                                            type="text"
                                            name="additionalComments"
                                            readOnly
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>
                                            {t('ssnNumber')}
                                        </Form.Label>
                                        <Form.Control
                                            value={profileData?.infoSSN || ''}
                                            type="text"
                                            name="infoSSN"
                                            readOnly
                                        />
                                    </Form.Group>
                                </Col>
                                {profileData?.userType === 'PATIENT' &&
                                    profileData?.userType === user?.userType ? (
                                    <Col md={12}>
                                        <Form.Group className="mb-3">
                                            <Form.Label style={{ whiteSpace: 'normal' }}>
                                                {t('sharePrescriptionToOptician')}
                                            </Form.Label>
                                            <Form.Control
                                                value={profileData?.infoPrescriptionToOptician ? t('yes') : t('no')}
                                                type="text"
                                                readOnly
                                            />
                                        </Form.Group>
                                    </Col>
                                ) : (
                                    ''
                                )}
                            </Row>
                        </Card.Body>
                    </Card>
                </div>
            </Container>
        </>
    );
};

export default PatientProfile;